import { useEffect } from 'react';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { NetworkProvider } from './context/NetworkContext';
import { Setup } from './pages/Setup';
import { Home } from './pages/Home';
import { StyleGuide } from './pages/StyleGuide';
import { track } from './lib/telemetry';
import { useDevice } from './hooks/useDevice';

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const device = useDevice();

  useEffect(() => {
    track('app_loaded', {
      device_kind: device.isTablet ? 'tablet' : 'phone',
      is_pwa_installed:
        typeof window !== 'undefined' &&
        window.matchMedia?.('(display-mode: standalone)').matches,
      viewport_w: device.screenWidth,
      viewport_h: device.screenHeight,
    });
    // Fire once per mount; subsequent device.* changes don't re-emit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-outline border-t-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Setup />;
  }

  return <Home />;
}

function App() {
  // Dev-only living reference for the MD3 primitives layer, not linked from
  // navigation. No router in this app — a plain pathname check, same as the
  // brainstorm's approach for keeping this a debug-only detour. Bypasses
  // AuthProvider/NetworkProvider entirely: the style guide is static and
  // doesn't need either, and skipping them avoids gating it behind the
  // auth-loading spinner AppContent shows below.
  if (window.location.pathname === '/dev/style-guide') {
    return <StyleGuide />;
  }

  return (
    <AuthProvider>
      <NetworkProvider>
        <AppContent />
      </NetworkProvider>
    </AuthProvider>
  );
}

export default App;
